import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as FinancialStatementsActions from './financial-statements.actions';
import { FinancialStatementsService } from '../../services/financial-statements.service';
import { selectSelectedStock } from '../stocks/stocks.selectors';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class FinancialStatementsEffects {
  loadFinancialStatements$: any = {};
  loadFinancialStatement$: any = {};
  createFinancialStatement$: any = {};
  updateFinancialStatement$: any = {};
  deleteFinancialStatement$: any = {};
  financialStatementSuccess$: any = {};
  constructor(
    private actions$: Actions,
    private financialStatementsService: FinancialStatementsService,
    private router: Router,
    private store: Store,
    private messageService: MessageService
  ) {
    this.loadFinancialStatements$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FinancialStatementsActions.loadFinancialStatements),
        switchMap(({ stockId, statementType = 'BalanceSheet', filter, fromDate, toDate }) =>
          this.financialStatementsService
            .getFinancialStatements(stockId, filter, statementType, 50, fromDate, toDate)
            .pipe(
              map((response) => {
                const financialStatements = response.data?.items || [];
                return FinancialStatementsActions.loadFinancialStatementsSuccess({
                  financialStatements,
                  statementType,
                });
              }),
              catchError((error) =>
                of(
                  FinancialStatementsActions.loadFinancialStatementsFailure({
                    error: error.message,
                    statementType,
                  })
                )
              )
            )
        )
      )
    );

    this.loadFinancialStatement$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FinancialStatementsActions.loadFinancialStatement),
        switchMap(({ id, statementType = 'BalanceSheet' }) =>
          this.financialStatementsService.getFinancialStatement(id, statementType).pipe(
            map((response) => {
              const financialStatement = response.data || response;
              return FinancialStatementsActions.loadFinancialStatementSuccess({
                financialStatement,
                statementType,
              });
            }),
            catchError((error) =>
              of(
                FinancialStatementsActions.loadFinancialStatementFailure({
                  error: error.message,
                  statementType,
                })
              )
            )
          )
        )
      )
    );

    this.createFinancialStatement$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FinancialStatementsActions.createFinancialStatement),
        switchMap(({ financialStatement, statementType = 'BalanceSheet' }) =>
          this.financialStatementsService
            .createFinancialStatement(financialStatement, statementType)
            .pipe(
              map((response) => {
                const createdFinancialStatement = response.data || response;
                return FinancialStatementsActions.createFinancialStatementSuccess({
                  financialStatement: createdFinancialStatement,
                  statementType,
                });
              }),
              catchError((error) =>
                of(
                  FinancialStatementsActions.createFinancialStatementFailure({
                    error: error.message,
                    statementType,
                  })
                )
              )
            )
        )
      )
    );

    this.updateFinancialStatement$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FinancialStatementsActions.updateFinancialStatement),
        switchMap(({ id, financialStatement, statementType = 'BalanceSheet' }) =>
          this.financialStatementsService
            .updateFinancialStatement(id, financialStatement, statementType)
            .pipe(
              map((response) => {
                const updatedFinancialStatement = response.data || response;
                return FinancialStatementsActions.updateFinancialStatementSuccess({
                  financialStatement: updatedFinancialStatement,
                  statementType,
                });
              }),
              catchError((error) =>
                of(
                  FinancialStatementsActions.updateFinancialStatementFailure({
                    error: error.message,
                    statementType,
                  })
                )
              )
            )
        )
      )
    );

    this.deleteFinancialStatement$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FinancialStatementsActions.deleteFinancialStatement),
        switchMap(({ id, statementType = 'BalanceSheet' }) =>
          this.financialStatementsService.deleteFinancialStatement(id, statementType).pipe(
            map(() =>
              FinancialStatementsActions.deleteFinancialStatementSuccess({
                id,
                statementType,
              })
            ),
            catchError((error) =>
              of(
                FinancialStatementsActions.deleteFinancialStatementFailure({
                  error: error.message,
                  statementType,
                })
              )
            )
          )
        )
      )
    );

    this.financialStatementSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(
            FinancialStatementsActions.createFinancialStatementSuccess,
            FinancialStatementsActions.updateFinancialStatementSuccess,
            FinancialStatementsActions.deleteFinancialStatementSuccess
          ),
          tap((action) => {
            this.store.select(selectSelectedStock).subscribe((stock) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Financial statement ${action.statementType} Operation successful`,
              });
              this.router.navigate([
                '/stocks',
                'view',
                stock?.id,
                'financial',
                action.statementType,
              ]);
            });
          })
        ),
      { dispatch: false }
    );
  }
}
